import { createStyles, Skeleton } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import moment from "moment";
import React from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Body, Label } from "../../../components/Typography";
import { getRoutePath } from "../../../hooks/useNavigateRoute";
import { formatCurrencyWithRegion } from "../../../utils/misc.util";
import { transactionTypeRender } from "../../../utils/searchField.util";
import { isNumber } from "../../../utils/type.util";

type Payment = NonNullable<GraphQL.PaymentQuery["payment"]>;
type RefundActor = NonNullable<Payment["refundDetails"]>["refundedBy"];

interface PaymentDetailsTabProps {
  payment: Payment | undefined;
  isLoading?: boolean;
}

const PLACEHOLDER = "—";
const SYSTEM_ACTOR = "System";

const PaymentMethodSchema = z
  .object({
    paidWith: z.string().nullish(),
    maskedCCNumber: z.string().nullish(),
    expiryDate: z.string().nullish(),
  })
  .passthrough();

type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

const useStyles = createStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[6],
    alignItems: "flex-start",
    width: "100%",
  },
  row: {
    display: "flex",
    gap: tokens.spacing[6],
    alignItems: "flex-start",
    paddingLeft: tokens.spacing[4],
    paddingRight: tokens.spacing[4],
    width: "100%",
  },
  field: {
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[1],
    flex: "1 1 0",
    minWidth: 0,
    margin: 0,
  },
  fieldLabel: {
    color: tokens.color.semantic.text.subtle,
    whiteSpace: "nowrap" as const,
  },
  fieldValue: {
    color: tokens.color.semantic.text.bold,
    marginInlineStart: 0,
  },
  organizationLink: {
    color: tokens.color.semantic.text.bold,
    fontFamily: tokens.font.family.sans,
    fontSize: tokens.font.size.base,
    "font-weight": tokens.font.weight.semibold,
    lineHeight: tokens.font.lineHeight.snug,
    textDecoration: "underline",
    textDecorationSkipInk: "none" as const,
    textUnderlinePosition: "from-font" as const,
  },
}));

function isRefund(transactionType: GraphQL.TransactionType | null | undefined): boolean {
  return transactionType === GraphQL.TransactionType.Refund;
}

function formatTransactionTypeLabel(transactionType: GraphQL.TransactionType | null | undefined): string {
  if (!transactionType) return PLACEHOLDER;
  return transactionTypeRender(transactionType);
}

function formatPaymentDate(createdAt: string | null | undefined): string {
  if (!createdAt) return PLACEHOLDER;
  return moment(createdAt).format("M/D/YYYY");
}

function parsePaymentMethod(paymentInfo: unknown): PaymentMethod {
  const result = PaymentMethodSchema.safeParse(paymentInfo);
  return result.success ? result.data : {};
}

function formatRefundActor(actor: RefundActor | null | undefined): string {
  if (!actor) return SYSTEM_ACTOR;
  const fullName = [actor.firstName, actor.lastName].filter(Boolean).join(" ");
  return fullName ? `${fullName} (${actor.email})` : actor.email;
}

function lastFourDigits(maskedCCNumber: string): string {
  return maskedCCNumber.slice(-4);
}

function formatPaidWith(paidWith: string): string {
  return paidWith
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const DetailField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const { classes } = useStyles();
  return (
    <dl className={classes.field}>
      <Label as="dt" weight="semibold" className={classes.fieldLabel}>
        {label}
      </Label>
      <Body as="dd" className={classes.fieldValue}>
        {children}
      </Body>
    </dl>
  );
};

const SkeletonField: React.FC = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.field}>
      <Skeleton height={14} width={100} mb={tokens.spacing[1]} />
      <Skeleton height={16} width={160} />
    </div>
  );
};

const PaymentDetailsSkeleton: React.FC = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.container} data-testid="payment-details-skeleton">
      <div className={classes.row}>
        <SkeletonField />
        <SkeletonField />
      </div>
      <div className={classes.row}>
        <SkeletonField />
        <SkeletonField />
      </div>
      <div className={classes.row}>
        <SkeletonField />
        <SkeletonField />
      </div>
    </div>
  );
};

const PaymentMethodValue: React.FC<{ method: PaymentMethod }> = ({ method }) => {
  const { paidWith, maskedCCNumber, expiryDate } = method;
  if (!paidWith && !maskedCCNumber && !expiryDate) return <>{PLACEHOLDER}</>;
  const cardLine = [paidWith && formatPaidWith(paidWith), maskedCCNumber && `x${lastFourDigits(maskedCCNumber)}`]
    .filter(Boolean)
    .join(" ");
  return (
    <>
      {cardLine && <div>{cardLine}</div>}
      {expiryDate && <div>Exp: {expiryDate}</div>}
    </>
  );
};

const OrganizationValue: React.FC<{ customerPublicId: string | null | undefined }> = ({ customerPublicId }) => {
  const { classes } = useStyles();
  const { data, isLoading, isError } = GraphQL.usePaymentOrganizationQuery(
    { customerPublicId: customerPublicId ?? "" },
    { enabled: !!customerPublicId }
  );
  if (!customerPublicId || isLoading || isError) return <>{PLACEHOLDER}</>;
  const organizationName = data?.customer.organizationName;
  return (
    <Link to={getRoutePath("customer", { publicId: customerPublicId })} className={classes.organizationLink}>
      {organizationName ?? customerPublicId}
    </Link>
  );
};

export const PaymentDetailsTab: React.FC<PaymentDetailsTabProps> = ({ payment, isLoading = false }) => {
  const { classes } = useStyles();

  if (isLoading) return <PaymentDetailsSkeleton />;
  if (!payment) return null;

  const refund = isRefund(payment.transactionType);
  const amountLabel = refund ? "Total Refund Amount" : "Total Payment Amount";
  const methodLabel = refund ? "Refund Method" : "Payment Method";
  const amountValue = isNumber(payment.amount) ? formatCurrencyWithRegion(payment.currency, payment.amount) : "";
  const paymentMethod = parsePaymentMethod(payment.paymentInfo);

  return (
    <div className={classes.container}>
      <div className={classes.row}>
        <DetailField label="Payment Date">{formatPaymentDate(payment.createdAt)}</DetailField>
        <DetailField label="Transaction Type">{formatTransactionTypeLabel(payment.transactionType)}</DetailField>
      </div>

      <div className={classes.row}>
        <DetailField label={amountLabel}>{amountValue || PLACEHOLDER}</DetailField>
        {refund ? (
          <DetailField label="Refunded By">{formatRefundActor(payment.refundDetails?.refundedBy)}</DetailField>
        ) : (
          <div className={classes.field} />
        )}
      </div>

      <div className={classes.row}>
        <DetailField label={methodLabel}>
          <PaymentMethodValue method={paymentMethod} />
        </DetailField>
        <DetailField label="Organization">
          <OrganizationValue customerPublicId={payment.customerPublicId} />
        </DetailField>
      </div>
    </div>
  );
};
